import { Channel } from "pusher-js";
import { usePusher } from "src/context/PusherContext";

// Track active subscriptions per channel
const channelSubscriptions = new Map<string, {
  channel: Channel;
  refCount: number;
}>();

/**
 * Hook that manages Pusher channel subscriptions with reference counting
 * to handle multiple components subscribing to the same channel
 */
export function usePusherChannelManager() {
  const pusher = usePusher();

  const subscribe = (channelId: string): Channel | null => {
    if (!pusher || !channelId) return null;

    const existing = channelSubscriptions.get(channelId);
    
    if (existing) {
      // Increment reference count for existing channel
      existing.refCount++;
      return existing.channel;
    }

    try {
      // Create new subscription
      const channel = pusher.subscribe(channelId);
      channelSubscriptions.set(channelId, {
        channel,
        refCount: 1
      });
      return channel;
    } catch (error) {
      console.error(`Failed to subscribe to channel ${channelId}:`, error);
      return null;
    }
  };

  const unsubscribe = (channelId: string): void => {
    if (!pusher || !channelId) return;

    const existing = channelSubscriptions.get(channelId);
    if (!existing) return;

    // Decrement reference count
    existing.refCount--;

    // Only unsubscribe when no more references
    if (existing.refCount <= 0) {
      try {
        pusher.unsubscribe(channelId);
        channelSubscriptions.delete(channelId);
      } catch (error) {
        console.error(`Failed to unsubscribe from channel ${channelId}:`, error);
      }
    }
  };

  return { subscribe, unsubscribe };
}